import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import type { LintFinding } from '../videoLinter';

import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { TextBackdrop } from 'js/src/luminary/text-contrast-utils';
import {
    calculateAllTextContrastsForVideo,
    TextBackdropKind,
} from 'js/src/luminary/text-contrast-utils';
import { meetsWCAG_AA, wcagAAThreshold } from 'js/src/utils/contrast-utils';

/**
 * Names what the text is drawn on and which dial actually moves it, so the fix does not start
 * by dimming media the reader cannot even see. Media brightness is only offered where dimming
 * media is genuinely the lever.
 */
function describeBackdrop(backdrop: TextBackdrop | null): string {
    if (!backdrop) {
        return '';
    }
    switch (backdrop.kind) {
        case TextBackdropKind.Media:
            return ` It sits directly on media ${backdrop.assetName}, so lower that media's brightness with ${TOOL_NAMES.editMedia} or recolour the text with ${TOOL_NAMES.setTextColor} (handle "text").`;
        case TextBackdropKind.ShapeBackdrop:
            return ` It sits on ${backdrop.assetName}, a shape the design draws. Recolour the text with ${TOOL_NAMES.setTextColor} (handle "text"), or recolour ${backdrop.assetName} with ${TOOL_NAMES.setAssetColor}.`;
        case TextBackdropKind.TextOwnBackground:
            return ` It sits on a background box the text asset draws for itself. Recolour that box with ${TOOL_NAMES.setTextColor} (the entry whose role is "backdrop" in this text's colour map), or recolour the text with ${TOOL_NAMES.setTextColor} (handle "text").`;
        case TextBackdropKind.GroupBackground:
            return ` It sits on a backdrop the ${backdrop.assetName} group draws behind its children. Recolour it with ${TOOL_NAMES.setTextColor} addressing the ${backdrop.assetName} group (the entry whose role is "backdrop" in that group's colour map), or recolour the text with ${TOOL_NAMES.setTextColor} (handle "text").`;
    }
}

/**
 * Error: text overlapping media has a contrast ratio below the WCAG AA
 * threshold, making it hard to read against its background. The threshold
 * depends on the text size: 3:1 for large text, 4.5:1 for normal text.
 */
export async function checkTextContrast(
    luminary: Luminary,
    _getState: GetRootState,
): Promise<LintFinding[]> {
    const findings: LintFinding[] = [];
    const canvas = document.createElement('canvas');

    const resultsBySceneId = await calculateAllTextContrastsForVideo(luminary, canvas);

    luminary.scenes.forEach((scene) => {
        const results = resultsBySceneId.get(scene.id);
        if (!results) {
            return;
        }
        results.forEach((result) => {
            if (meetsWCAG_AA(result.contrastRatio, result.isLargeText)) {
                return;
            }
            const ratio = result.contrastRatio.toFixed(1);
            const threshold = wcagAAThreshold(result.isLargeText);
            const tier = result.isLargeText ? 'large text' : 'normal-size text';
            const text = result.textContent.trim();
            findings.push({
                check: 'text_contrast',
                severity: 'error',
                sceneId: scene.id,
                message: `${result.textAssetName} has low text contrast (${ratio}:1, below the ${threshold}:1 minimum for ${tier})${
                    text ? ` for "${text}"` : ''
                }.${describeBackdrop(result.backdrop)} Load the ${SKILL_NAMES.fixTextContrast} skill for the fix procedure.`,
            });
        });
    });

    return findings;
}
